import React, { useContext, useEffect } from 'react';
import { Head, Wrapper, EnterPlates, IHelpContent } from '@adac-mp/components';
import { VignetteContext, IPlatesValidatedState, ITypeVignetteOrderingProcessFields } from '@adac-mp/shared';
import { useValidatedStates } from '@adac-mp/components/src/hooks/useValidatedStates';
import { withTracking } from '@adac-mp/components/src/utils/tracking/tracking';

interface IPlatesSelectionProps {
	hasError?: boolean;
	helpContent?: IHelpContent;
	hasSpecialPlates?: boolean;
	supplierHasMultipleProducts?: boolean;
	updateValidatedStepsMap?: (hasError: boolean, reset?: boolean) => void;
}

const PlatesSelectionComponent = ({
	hasError,
	helpContent,
	hasSpecialPlates = false,
	supplierHasMultipleProducts,
	updateValidatedStepsMap = () => {},
}: IPlatesSelectionProps) => {
	const { selectedProducts, selectedSectionTolls, contentfulLabels, updatePlates } = useContext(VignetteContext);
	const currentLabels = contentfulLabels as ITypeVignetteOrderingProcessFields;
	const [state, { updatePlatesWithConfirmationAndError, updateWithError }] = useValidatedStates({
		plates: { value: selectedProducts[0]?.plates ?? ['', '', ''], error: false },
	});

	const { value, error, confirmed, countryCode, specialPlates } = state.plates;

	const handlePlates = ({
		value: updatedValue,
		error: updatedError,
		confirmed: updatedConfirmed,
		countryCode: updatedCountryCode,
		specialPlates: updatedSpecialPlates,
	}: IPlatesValidatedState) => {
		if (selectedProducts[0]) {
			if (Array.isArray(updatedValue)) {
				updatePlatesWithConfirmationAndError(
					'plates',
					updatedValue,
					updatedError,
					updatedConfirmed ?? false,
					updatedCountryCode,
					updatedSpecialPlates,
				);
				updatePlates(updatedValue, countryCode, Number(specialPlates ?? 0));
			}
		}
	};

	const isConfigurationValid = () => {
		if (!value || error || !confirmed || value?.some((field: string) => field === '')) {
			return false;
		}
		return true;
	};

	useEffect(() => {
		if (!selectedProducts[0]) {
			updatePlatesWithConfirmationAndError('plates', '', false, false, countryCode, specialPlates);
		}
		updateValidatedStepsMap(isConfigurationValid());
	}, [error, value, confirmed, selectedProducts, selectedSectionTolls]);

	useEffect(() => {
		if (hasError) {
			const isInvalid = value.join('') === '' || error;
			updateWithError('plates', value, isInvalid);
		}
	}, [hasError]);

	return (
		<Wrapper animate paddingTop={`${supplierHasMultipleProducts ? 'large' : 'small'}`}>
			<Head
				headline={currentLabels?.cart?.licensePlates?.headlineLicenseplate || 'Wie lautet Ihr Kennzeichen?'}
				helpContent={helpContent}
			/>
			<div className="md:w-6/12 md:mx-auto">
				<EnterPlates
					hasSpecialPlates={hasSpecialPlates}
					plates={state.plates}
					setPlates={handlePlates}
					hasError={hasError}
				/>
			</div>
		</Wrapper>
	);
};

export const PlatesSelection = withTracking(PlatesSelectionComponent);
